import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, Reorder, motion } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import Icon from '../components/Icon';
import IncomeChooser from '../components/IncomeChooser';
import { ChipSelect, Field, Modal, Page, PageHead, Select, Tabs } from '../components/UI';
import { useApp } from '../context/AppContext';
import { api } from '../utils/api';
import { HEIGHTS, toDateInput, withOption } from '../utils/format';

const TABS = [
  { value: 'basics', label: 'Basics', icon: 'user' },
  { value: 'career', label: 'Career', icon: 'cap' },
  { value: 'family', label: 'Family', icon: 'family' },
  { value: 'horoscope', label: 'Horoscope', icon: 'moon' },
  { value: 'about', label: 'About', icon: 'heart' },
  { value: 'photos', label: 'Photos', icon: 'camera' },
];

const SYNC_FIELDS = [
  ['firstName', 'First name'], ['lastName', 'Last name'], ['dob', 'Date of birth'], ['maritalStatus', 'Marital status'],
  ['phone', 'Phone'], ['city', 'City'], ['state', 'State'], ['homeTown', 'Hometown'], ['motherTongue', 'Mother tongue'],
  ['languages', 'Languages'], ['highestQualification', 'Highest qualification'], ['education', 'Education'],
  ['specialty', 'Specialty'], ['headline', 'Headline'], ['designation', 'Designation'], ['employer', 'Employer'],
  ['experienceYears', 'Experience (yrs)'], ['workHistory', 'Work history'], ['skills', 'Skills'], ['certifications', 'Certifications'],
  ['about', 'About'], ['photos', 'Profile photo'],
];

const show = (v) => {
  if (Array.isArray(v)) return v.length ? (typeof v[0] === 'object' ? `${v.length} entr${v.length > 1 ? 'ies' : 'y'}` : v.join(', ')) : '—';
  if (v && /^\d{4}-\d{2}-\d{2}T/.test(v)) return new Date(v).toLocaleDateString('en-IN');
  return v === '' || v === null || v === undefined ? '—' : String(v);
};
const norm = (v) => JSON.stringify(v && /^\d{4}-\d{2}-\d{2}T/.test(v) ? v.slice(0, 10) : v ?? '');
const empty = (v) => v === '' || v === null || v === undefined || (Array.isArray(v) && !v.length) || v === 0;

function MedhireSync({ open, onClose }) {
  const { user, setUser, toast } = useApp();
  const [prefill, setPrefill] = useState(null);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPrefill(null);
    api('/user/medhire/refresh')
      .then((d) => {
        setUser(d.user);
        setPrefill(d.prefill);
      })
      .catch((e) => {
        toast(e.message, 'error');
        onClose();
      });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const diffs = useMemo(() => {
    if (!prefill) return [];
    return SYNC_FIELDS.filter(([k]) => {
      if (empty(prefill[k])) return false;
      if (k === 'photos') return !(user.photos || []).includes(prefill.photos[0]);
      return norm(prefill[k]) !== norm(user[k]);
    });
  }, [prefill, user]);

  useEffect(() => setPicked(diffs.map(([k]) => k).filter((k) => !['about', 'photos'].includes(k))), [diffs]);

  const apply = async () => {
    setBusy(true);
    const body = Object.fromEntries(picked.map((k) => [k, prefill[k]]));
    if (body.photos) body.photos = [prefill.photos[0], ...(user.photos || [])];
    try {
      const r = await api('/user/me', { method: 'PUT', body });
      setUser(r.user);
      toast(`Updated ${picked.length} field${picked.length === 1 ? '' : 's'} from MedHire`, 'success');
      onClose();
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Sync from MedHire" width={640}>
      {!prefill ? (
        <div className="sync__loading"><motion.span animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}><Icon name="refresh" size={26} /></motion.span> Reading your MedHire profile…</div>
      ) : !diffs.length ? (
        <div className="gate">
          <div className="gate__icon tint-teal"><Icon name="check" size={28} stroke={2.4} /></div>
          <p>Everything is already in sync with MedHire.</p>
          <small className="muted">Salary on MedHire: {prefill.medhireSalary?.present ? `₹${prefill.medhireSalary.present.toLocaleString('en-IN')}/month` : 'not set'} — choose how to show it under Career.</small>
        </div>
      ) : (
        <>
          <p className="muted">Pick the details you'd like to copy into CTshaadi. Nothing changes on MedHire.</p>
          <div className="sync">
            {diffs.map(([k, label]) => (
              <label key={k} className={`sync__row ${picked.includes(k) ? 'is-on' : ''}`}>
                <input type="checkbox" checked={picked.includes(k)} onChange={() => setPicked(picked.includes(k) ? picked.filter((x) => x !== k) : [...picked, k])} />
                <span className="sync__label">{label}</span>
                <span className="sync__vals">
                  <span className="sync__old">{show(user[k])}</span>
                  <Icon name="arrowRight" size={14} />
                  <span className="sync__new">{k === 'photos' ? <img src={prefill.photos[0]} alt="" /> : show(prefill[k])}</span>
                </span>
              </label>
            ))}
          </div>
          <button className="btn btn--teal btn--block" disabled={!picked.length || busy} onClick={apply}>
            {busy ? 'Updating…' : `Update ${picked.length} selected`}
          </button>
        </>
      )}
    </Modal>
  );
}

function Photos() {
  const { user, setUser, toast } = useApp();
  const [photos, setPhotos] = useState(user.photos || []);
  const [busy, setBusy] = useState(false);
  useEffect(() => setPhotos(user.photos || []), [user.photos]);

  const save = async (next) => {
    setPhotos(next);
    try {
      const r = await api('/user/me', { method: 'PUT', body: { photos: next } });
      setUser(r.user);
    } catch (e) {
      toast(e.message, 'error');
    }
  };
  const upload = async (files) => {
    if (!files.length) return;
    setBusy(true);
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append('photos', f));
    try {
      const r = await api('/user/me/photos', { method: 'POST', form: fd });
      setUser(r.user);
      toast('Photos uploaded', 'success');
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <p className="muted">Drag to reorder — the first photo is your profile picture. Up to 8 photos.</p>
      <Reorder.Group axis="x" values={photos} onReorder={setPhotos} className="photo-grid" as="div">
        {photos.map((src, i) => (
          <Reorder.Item key={src} value={src} as="div" className="photo-grid__item" onDragEnd={() => save(photos)} whileDrag={{ scale: 1.06, zIndex: 3 }}>
            <img src={src} alt="" draggable={false} />
            {i === 0 && <span className="photo-grid__main">Profile photo</span>}
            <div className="photo-grid__tools">
              {i > 0 && <button onClick={() => save([src, ...photos.filter((p) => p !== src)])} title="Make profile photo"><Icon name="star" size={14} /></button>}
              <button onClick={() => save(photos.filter((p) => p !== src))} title="Remove"><Icon name="trash" size={14} /></button>
            </div>
          </Reorder.Item>
        ))}
        {photos.length < 8 && (
          <label className="photo-picker__add photo-grid__add">
            <Icon name={busy ? 'refresh' : 'camera'} size={26} />
            <small>{busy ? 'Uploading…' : 'Add photos'}</small>
            <input type="file" accept="image/jpeg,image/png,image/webp" multiple hidden disabled={busy} onChange={(e) => upload(e.target.files)} />
          </label>
        )}
      </Reorder.Group>
    </div>
  );
}

export default function EditProfile() {
  const { user, setUser, meta, toast } = useApp();
  const o = meta?.options || {};
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'basics';
  const [f, setF] = useState(() => ({ ...user, dob: toDateInput(user.dob) }));
  const [busy, setBusy] = useState(false);
  const [sync, setSync] = useState(params.get('sync') === '1');

  useEffect(() => setF((cur) => ({ ...cur, ...user, dob: toDateInput(user.dob) })), [user]);

  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));
  const setIn = (group, k) => (v) => setF((s) => ({ ...s, [group]: { ...(s[group] || {}), [k]: v?.target ? v.target.value : v } }));
  const setEdu = (k) => (e) => setF((s) => ({ ...s, education: [{ ...(s.education?.[0] || {}), [k]: e.target.value }, ...(s.education || []).slice(1)] }));

  const save = async () => {
    setBusy(true);
    const { photos, partnerPrefs, privacy, ...rest } = f;
    try {
      const r = await api('/user/me', { method: 'PUT', body: { ...rest, heightCm: Number(f.heightCm) || undefined, experienceYears: Number(f.experienceYears) || 0 } });
      setUser(r.user);
      toast('Profile saved ✨', 'success');
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page>
      <div className="container narrow">
        <PageHead eyebrow={`Profile ${user.profileCompletion}% complete`} title="Edit your profile" sub="Great profiles get up to 8× more interests.">
          <Link to={`/profile/${user.profileId}`} className="btn btn--ghost btn--sm"><Icon name="eye" size={16} /> Preview</Link>
        </PageHead>

        <div className="progress"><motion.div className="progress__bar" initial={{ width: 0 }} animate={{ width: `${user.profileCompletion}%` }} transition={{ duration: 1 }} /></div>

        {user.medhireId && (
          <motion.div className="medhire-strip" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <span className="medhire-chip"><Icon name="steth" size={13} /> MedHire linked</span>
            <span className="muted">Last synced {user.medhireSyncedAt ? new Date(user.medhireSyncedAt).toLocaleDateString('en-IN') : '—'}</span>
            <button className="btn btn--teal btn--sm" onClick={() => setSync(true)}><Icon name="refresh" size={15} /> Sync from MedHire</button>
          </motion.div>
        )}

        <Tabs id="edit" tabs={TABS} value={tab} onChange={(v) => setParams({ tab: v })} />

        <AnimatePresence mode="wait">
          <motion.div key={tab} className="card form-card" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.22 }}>
            {tab === 'basics' && (
              <>
                <div className="row-2">
                  <Field label="First name"><input className="input" value={f.firstName || ''} onChange={set('firstName')} /></Field>
                  <Field label="Last name"><input className="input" value={f.lastName || ''} onChange={set('lastName')} /></Field>
                </div>
                <div className="row-3">
                  <Field label="Date of birth"><input className="input" type="date" value={f.dob || ''} onChange={set('dob')} /></Field>
                  <Field label="Height"><Select value={f.heightCm} onChange={set('heightCm')} options={HEIGHTS.map((h) => ({ value: h.cm, label: h.label }))} /></Field>
                  <Field label="Marital status"><Select value={f.maritalStatus} onChange={set('maritalStatus')} options={o.MARITAL || []} /></Field>
                </div>
                <div className="row-3">
                  <Field label="Religion"><Select value={f.religion} onChange={set('religion')} options={o.RELIGIONS || []} /></Field>
                  <Field label="Community / caste"><input className="input" value={f.caste || ''} onChange={set('caste')} /></Field>
                  <Field label="Mother tongue"><Select value={f.motherTongue} onChange={set('motherTongue')} options={withOption(o.MOTHER_TONGUES, f.motherTongue)} /></Field>
                </div>
                <div className="row-3">
                  <Field label="Diet"><Select value={f.diet} onChange={set('diet')} options={o.DIETS || []} /></Field>
                  <Field label="Smoke"><Select value={f.smoke} onChange={set('smoke')} options={['No', 'Occasionally', 'Yes']} /></Field>
                  <Field label="Drink"><Select value={f.drink} onChange={set('drink')} options={['No', 'Occasionally', 'Yes']} /></Field>
                </div>
                <div className="row-3">
                  <Field label="City"><input className="input" value={f.city || ''} onChange={set('city')} /></Field>
                  <Field label="State"><input className="input" value={f.state || ''} onChange={set('state')} /></Field>
                  <Field label="Hometown"><input className="input" value={f.homeTown || ''} onChange={set('homeTown')} /></Field>
                </div>
                <div className="row-2">
                  <Field label="Mobile number"><input className="input" value={f.phone || ''} onChange={set('phone')} /></Field>
                  <Field label="Profile created by"><Select value={f.createdBy} onChange={set('createdBy')} options={o.CREATED_BY || []} /></Field>
                </div>
              </>
            )}

            {tab === 'career' && (
              <>
                <div className="row-2">
                  <Field label="Highest qualification"><Select value={f.highestQualification} onChange={set('highestQualification')} options={withOption(o.QUALIFICATIONS, f.highestQualification)} /></Field>
                  <Field label="College / University"><input className="input" value={f.education?.[0]?.institution || ''} onChange={setEdu('institution')} /></Field>
                </div>
                <div className="row-2">
                  <Field label="Profession"><Select value={f.profession} onChange={set('profession')} options={withOption(o.PROFESSIONS, f.profession)} /></Field>
                  <Field label="Specialty"><input className="input" value={f.specialty || ''} onChange={set('specialty')} /></Field>
                </div>
                <div className="row-3">
                  <Field label="Designation"><input className="input" value={f.designation || ''} onChange={set('designation')} /></Field>
                  <Field label="Hospital / employer"><input className="input" value={f.employer || ''} onChange={set('employer')} /></Field>
                  <Field label="Experience (yrs)"><input className="input" type="number" min="0" value={f.experienceYears ?? ''} onChange={set('experienceYears')} /></Field>
                </div>
                <h3 className="sub-h">Income</h3>
                <IncomeChooser value={f.income || {}} onChange={set('income')} medhireSalary={user.medhireSalary} />
              </>
            )}

            {tab === 'family' && (
              <>
                <div className="row-2">
                  <Field label="Father's occupation"><input className="input" value={f.family?.fatherOccupation || ''} onChange={setIn('family', 'fatherOccupation')} /></Field>
                  <Field label="Mother's occupation"><input className="input" value={f.family?.motherOccupation || ''} onChange={setIn('family', 'motherOccupation')} /></Field>
                </div>
                <div className="row-2">
                  <Field label="Brothers"><input className="input" type="number" min="0" value={f.family?.brothers ?? 0} onChange={setIn('family', 'brothers')} /></Field>
                  <Field label="Sisters"><input className="input" type="number" min="0" value={f.family?.sisters ?? 0} onChange={setIn('family', 'sisters')} /></Field>
                </div>
                <div className="row-3">
                  <Field label="Family type"><Select value={f.family?.familyType} onChange={setIn('family', 'familyType')} options={o.FAMILY_TYPES || []} /></Field>
                  <Field label="Family values"><Select value={f.family?.familyValues} onChange={setIn('family', 'familyValues')} options={o.FAMILY_VALUES || []} /></Field>
                  <Field label="Native place"><input className="input" value={f.family?.nativePlace || ''} onChange={setIn('family', 'nativePlace')} /></Field>
                </div>
              </>
            )}

            {tab === 'horoscope' && (
              <>
                <div className="row-2">
                  <Field label="Rashi (moon sign)"><Select value={f.horoscope?.rashi} onChange={setIn('horoscope', 'rashi')} options={o.RASHIS || []} /></Field>
                  <Field label="Nakshatra"><Select value={f.horoscope?.nakshatra} onChange={setIn('horoscope', 'nakshatra')} options={o.NAKSHATRAS || []} /></Field>
                </div>
                <div className="row-3">
                  <Field label="Manglik"><Select value={f.horoscope?.manglik} onChange={setIn('horoscope', 'manglik')} options={o.MANGLIK || []} /></Field>
                  <Field label="Birth time"><input className="input" type="time" value={f.horoscope?.birthTime || ''} onChange={setIn('horoscope', 'birthTime')} /></Field>
                  <Field label="Birth place"><input className="input" value={f.horoscope?.birthPlace || ''} onChange={setIn('horoscope', 'birthPlace')} /></Field>
                </div>
                <p className="note"><Icon name="moon" size={16} /> Your horoscope powers the Kundli score shown on every profile.</p>
              </>
            )}

            {tab === 'about' && (
              <>
                <Field label="About me" hint={`${(f.about || '').length}/1000`}>
                  <textarea className="input" rows={6} maxLength={1000} value={f.about || ''} onChange={set('about')} />
                </Field>
                <Field label="Hobbies & interests"><ChipSelect options={o.HOBBIES || []} value={f.hobbies || []} onChange={set('hobbies')} anyLabel={null} /></Field>
                <Field label="Languages I speak"><ChipSelect options={withOption(o.MOTHER_TONGUES, null)} value={f.languages || []} onChange={set('languages')} anyLabel={null} /></Field>
              </>
            )}

            {tab === 'photos' && <Photos />}

            {tab !== 'photos' && (
              <div className="form-card__foot">
                <motion.button whileTap={{ scale: 0.96 }} className="btn btn--primary btn--lg" onClick={save} disabled={busy}>
                  {busy ? 'Saving…' : 'Save changes'}
                </motion.button>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
      <MedhireSync open={sync} onClose={() => setSync(false)} />
    </Page>
  );
}
